// The door/window (OPENINGS) domain — selection, hover, and the Select-chain
// gesture hooks (press steps 0a–0c, the resize/slide drags, the release
// lock-toggle, cancel). Follows the measure slice's pattern: this slice owns
// the state + bodies; floor-mode keeps the chain's ORDER and calls the hooks at
// the original priority spots. The opening VISUALS (handles/border/dims pills)
// and the dim-edit plumbing are still in floor-mode (next E4 step) — they read
// this slice's selection/hover through the accessors.

import * as THREE from "three";
import { floorScene, placementSystem, wallSystem } from "../core";
import { WALL_THICKNESS } from "../walls";
import { type SpriteLabel, makeLabel, setLabel } from "../sprite-label";
import { BORDER_SCALE, VERTEX_FREE, VERTEX_LOCKED, VERTEX_SELECTED } from "../vertex-style";
import { M_TO_IN, formatLengthIn, getDisplayUnit } from "../../model/units";
import { uiState } from "../../ui/state";
import { desktopMouse } from "../../input/desktop";
import { type FloorTool as FloorToolId } from "../../ui/floor-ui";

// One pill per editable field. `field` drives picking + the in-place editor.
export type OpeningField =
  | "width"
  | "height"
  | "leftGap"
  | "rightGap"
  | "sillGap"
  | "topGap";

export interface OpeningRef {
  wallId: number;
  openingId: number;
}
export type OpeningEdge = "left" | "right" | "top" | "bottom";
export interface OpeningEdgeRef extends OpeningRef {
  edge: OpeningEdge;
}

// Two opening refs naming the same opening (either may be null).
export const sameOpening = (
  a: OpeningRef | null,
  b: OpeningRef | null,
): boolean => a !== null && b !== null && a.wallId === b.wallId && a.openingId === b.openingId;

export interface OpeningsDomainDeps {
  activeCamera: () => THREE.Camera;
  canvasRect: () => DOMRect; // renderer canvas bounds, for screen-space picks
  activeTool: () => FloorToolId; // hover/selection are Select-tool affordances
  // Selecting an opening drops wall/placement/measurement AND any marquee group
  // (one selection at a time), then refreshes the selection UI.
  dropOtherSelections: () => void;
  refreshSelectionUI: () => void; // dims/fields follow a resize/slide live
  commitFloor: () => void; // the release lock-toggle is undoable
  // Constant on-screen pill sizing (shared with the wall/measure dim systems).
  pillScale: (p: THREE.Vector3) => number;
  // The field of OUR selected opening being edited in the shared dim-edit box
  // (its pill hides while the box stands in), or null.
  editingOpeningField: (openingId: number) => OpeningField | null;
  // Extra perpendicular distance (metres) to push a wall's PLAN-view opening
  // dimensions further off the wall, so they clear the wall's OWN dimension pill
  // when both land on the same face. Returns 0 (no nudge) unless that wall dim is
  // shown AND sits on the same side as the opening chain. Zoom-aware (tracks the
  // wall pill's on-screen-constant size), so they stay clear at any zoom.
  wallDimPlanPush: (wallId: number) => number;
}

export interface OpeningsDomainApi {
  // State accessors (visuals + dim-edit + keyboard actions read these).
  selected: () => OpeningRef | null;
  setSelected: (sel: OpeningRef | null) => void;
  hover: () => OpeningRef | null;
  hoverHandle: () => OpeningEdgeRef | null;
  clearSelection: () => void; // drop selection + any armed edge-resize
  // Doors/windows are viewed/edited looking at the wall face: 3D from any
  // angle; in 2D only from a side elevation (not the top-down plan).
  viewable: () => boolean;
  // Visuals (per-frame): the white border + resize handles, and the selected
  // opening's dimension lines + editable pills.
  updateHandles: () => void;
  updateDims: () => void;
  // "Show all dimensions" overlay: width (+ height off-plan) labels for EVERY
  // opening, independent of selection. setShowAllDims drives the toggle;
  // updateAllDims renders it each frame.
  setShowAllDims: (on: boolean) => void;
  updateAllDims: () => void;
  // Dim-edit integration (the shared editor box lives in floor-mode).
  fieldValue: (wallId: number, openingId: number, field: OpeningField) => number | null;
  applyField: (wallId: number, openingId: number, field: OpeningField, m: number) => void;
  pickPillField: (ray: THREE.Raycaster) => OpeningField | null;
  pillPosition: (field: OpeningField) => THREE.Vector3;
  editorVisible: () => boolean;
  // Per-frame + Select-chain hooks (called at the chain's original spots).
  updateHover: () => void;
  press: () => boolean; // steps 0a–0c; true = the press was consumed
  drag: () => boolean; // resize/slide drag; true = claimed this frame
  gestureArmed: () => boolean; // an opening drag/resize is armed (wasDragging)
  clearGesture: () => void; // press-preamble / bulk-release / cancel reset
  release: () => void; // plain click on a resize handle toggles its lock
}

export function installOpeningsDomain(deps: OpeningsDomainDeps): OpeningsDomainApi {
  // Sliding an opening along its wall — armed by a press anywhere on the opening
  // BODY (not on a resize handle). A drag moves it; a plain click just selects it.
  let openingDrag: OpeningRef | null = null;
  let openingDragMoved = false; // the slide cleared the dead-zone (vs a plain select-click)
  const _dragStart = new THREE.Vector2();
  // The door/window currently selected — its 4 resize handles + dimensions show
  // and its border is white while selected.
  let selectedOpening: OpeningRef | null = null;
  // The opening the cursor is hovering (Select tool) — its border turns white.
  // Plus the single resize handle directly under the cursor (only when the opening
  // is selected), for the white hover ring. Both recomputed each frame.
  let hoverOpening: OpeningRef | null = null;
  let hoverOpeningHandle: OpeningEdgeRef | null = null;
  // The active edge resize drag (a press on a resize handle).
  let openingResize: OpeningEdgeRef | null = null;

  const _ray = new THREE.Raycaster();
  const _ovDir = new THREE.Vector3();
  const _ohScreen = new THREE.Vector3();
  const _tmpVec3 = new THREE.Vector3();

  // ── Visuals: dimension editor + handles + border (parented to the floor) ──
  const OPENING_EDGES = ["left", "right", "top", "bottom"] as const;
  // Dimension lines. Move shows the most: width + L/R gaps above (3 spans) and
  // height + head/sill gaps off the end (3 spans) = 6 spans × (main + 2 ticks) =
  // 18 segs → 36 verts. Sized with headroom; drawn count via setDrawRange.
  const _dimGeo = new THREE.BufferGeometry();
  _dimGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(48 * 3), 3));
  const dimLines = new THREE.LineSegments(
    _dimGeo,
    new THREE.LineBasicMaterial({ color: 0xffe87a, depthTest: false, transparent: true, opacity: 0.85 }),
  );
  dimLines.renderOrder = 16;
  dimLines.frustumCulled = false;
  const editorGroup = new THREE.Group();
  editorGroup.visible = false;
  editorGroup.add(dimLines);
  floorScene.root.add(editorGroup);
  const pills: Record<OpeningField, SpriteLabel> = {
    width: makeLabel(0.5, 0.155, 18),
    height: makeLabel(0.5, 0.155, 18),
    leftGap: makeLabel(0.5, 0.155, 18),
    rightGap: makeLabel(0.5, 0.155, 18),
    sillGap: makeLabel(0.5, 0.155, 18),
    topGap: makeLabel(0.5, 0.155, 18),
  };
  for (const [field, l] of Object.entries(pills)) {
    l.sprite.userData.field = field;
    editorGroup.add(l.sprite);
  }

  // ── "Show all dimensions" overlay ──
  // A read-only overlay of width (+ height off the top-down plan) labels for
  // EVERY opening, independent of selection — driven by the floor "Show" toggle.
  // It owns a SEPARATE pooled label set + line buffer (the editorGroup pills
  // above are a fixed six, for the one selected opening), so it can annotate many
  // openings at once. View-filtered like the editor: the bird's-eye plan shows
  // only the along-wall width; a side elevation / 3D adds the height, and an
  // elevation skips openings on edge-on walls (you read the dimensions on the
  // side you're facing). The selected opening is skipped here so the editor's
  // richer pills aren't doubled.
  let showAllDims = false;
  const allDimsGroup = new THREE.Group();
  allDimsGroup.visible = false;
  floorScene.root.add(allDimsGroup);
  const _allDimGeo = new THREE.BufferGeometry();
  let allDimCap = 0; // current vertex capacity of the line buffer
  const ensureAllDimCap = (verts: number): void => {
    if (verts <= allDimCap) return;
    allDimCap = Math.max(verts, allDimCap * 2, 96);
    _allDimGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(allDimCap * 3), 3));
  };
  ensureAllDimCap(96);
  const allDimLines = new THREE.LineSegments(
    _allDimGeo,
    new THREE.LineBasicMaterial({ color: 0xffe87a, depthTest: false, transparent: true, opacity: 0.8 }),
  );
  allDimLines.renderOrder = 16;
  allDimLines.frustumCulled = false;
  allDimsGroup.add(allDimLines);
  const allDimLabels: SpriteLabel[] = [];
  const allDimLabel = (idx: number): SpriteLabel => {
    while (allDimLabels.length <= idx) {
      const l = makeLabel(0.5, 0.155, 16);
      allDimsGroup.add(l.sprite);
      allDimLabels.push(l);
    }
    return allDimLabels[idx];
  };

  // Resize-handle pool — fixed world size (matches the wall grab spheres ≈
  // 0.0765) so they scale with the scene/zoom. Each door/window has FOUR
  // (left/right jambs, head, sill) — shown only while the opening is SELECTED.
  // Yellow by default, red when locked, a WHITE border ring on hover.
  const _ohGeo = new THREE.SphereGeometry(0.0765, 14, 10);
  const _ohOutlineGeo = new THREE.SphereGeometry(0.0765 * BORDER_SCALE, 16, 12);
  // transparent: true (at full opacity) puts these in the transparent pass so
  // their renderOrder lifts them OVER the window glass (also transparent),
  // instead of being opaque-pass geometry the glass then blends on top of.
  const _resizeMat = new THREE.MeshBasicMaterial({ color: VERTEX_FREE, depthTest: false, transparent: true });
  const _lockMat = new THREE.MeshBasicMaterial({ color: VERTEX_LOCKED, depthTest: false, transparent: true });
  const handleGroup = new THREE.Group();
  floorScene.root.add(handleGroup);
  const handlePool: THREE.Mesh[] = [];
  const poolHandle = (idx: number): THREE.Mesh => {
    while (handlePool.length <= idx) {
      const m = new THREE.Mesh(_ohGeo, _resizeMat);
      m.renderOrder = 17;
      handleGroup.add(m);
      handlePool.push(m);
    }
    return handlePool[idx];
  };
  // One white ring behind the hovered resize handle (at most one is hovered).
  const _hoverOutlineMat = new THREE.MeshBasicMaterial({ color: VERTEX_SELECTED, depthTest: false, transparent: true });
  const hoverOutline = new THREE.Mesh(_ohOutlineGeo, _hoverOutlineMat);
  hoverOutline.renderOrder = 15; // behind the coloured handle (17) — only its rim shows
  hoverOutline.visible = false;
  handleGroup.add(hoverOutline);
  // White rectangle outline around the hovered/selected opening — its "border
  // turns white". Rebuilt each frame from the opening's jamb/sill/head corners.
  const _borderGeo = new THREE.BufferGeometry();
  _borderGeo.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(48), 3));
  const border = new THREE.LineSegments(
    _borderGeo,
    new THREE.LineBasicMaterial({ color: VERTEX_SELECTED, depthTest: false, transparent: true }),
  );
  border.renderOrder = 16;
  border.frustumCulled = false; // geometry rebuilt each frame; bounds go stale
  border.visible = false;
  handleGroup.add(border);

  // Write one opening's rectangle outline (4 edges) into the border line
  // buffer, starting at vertex `v`; returns the next free vertex index.
  function writeBorder(bp: THREE.BufferAttribute, v: number, wid: number, oid: number): number {
    const g = wallSystem.openingGeometry(wid, oid);
    if (!g) return v;
    const { cx, cz, ux, uz, halfW, sill, top } = g;
    const lx = cx - ux * halfW; // left jamb foot (XZ)
    const lz = cz - uz * halfW;
    const rx = cx + ux * halfW; // right jamb foot
    const rz = cz + uz * halfW;
    const e = (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): void => {
      bp.setXYZ(v++, x0, y0, z0);
      bp.setXYZ(v++, x1, y1, z1);
    };
    e(lx, sill, lz, rx, sill, rz); // sill
    e(rx, sill, rz, rx, top, rz); // right jamb
    e(rx, top, rz, lx, top, lz); // head
    e(lx, top, lz, lx, sill, lz); // left jamb
    return v;
  }

  // Which view we're rendering the opening's editing UI in: 3D, a 2D side
  // elevation, or the 2D top-down bird's-eye plan. Doors face the camera in 3D
  // and elevation; in plan we only show the along-wall (left/width/right)
  // measurements laid flat on the floor.
  function dimView(): "3d" | "elevation" | "plan" {
    if (uiState.floorView === "3d") return "3d";
    deps.activeCamera().getWorldDirection(_ovDir);
    return Math.abs(_ovDir.y) < 0.5 ? "elevation" : "plan";
  }

  // Handles/border/picking only show where the door faces the camera (3D or a
  // 2D side elevation) — never in the top-down plan.
  function viewable(): boolean {
    return dimView() !== "plan";
  }

  // The resize handle under the cursor — only the SELECTED opening's four are
  // pickable. Screen-space proximity (forgiving px) so the small dots are easy.
  function pickResizeHandleScreen(px: number, py: number, maxPx = 16): OpeningEdgeRef | null {
    if (!selectedOpening) return null;
    const g = wallSystem.openingGeometry(selectedOpening.wallId, selectedOpening.openingId);
    if (!g) return null;
    const { cx, cz, ux, uz, halfW, sill, top } = g;
    const midY = (sill + top) / 2;
    const cam = deps.activeCamera();
    cam.updateMatrixWorld();
    const r = deps.canvasRect();
    let bestEdge: OpeningEdge | null = null;
    let bestD = maxPx;
    const consider = (edge: OpeningEdge, x: number, y: number, z: number): void => {
      _ohScreen.set(x, y, z).project(cam);
      if (_ohScreen.z > 1) return; // behind the camera
      const sx = (_ohScreen.x * 0.5 + 0.5) * r.width + r.left;
      const sy = (-_ohScreen.y * 0.5 + 0.5) * r.height + r.top;
      const d = Math.hypot(sx - px, sy - py);
      if (d < bestD) {
        bestD = d;
        bestEdge = edge;
      }
    };
    consider("left", cx - ux * halfW, midY, cz - uz * halfW);
    consider("right", cx + ux * halfW, midY, cz + uz * halfW);
    consider("top", cx, top, cz);
    consider("bottom", cx, sill, cz);
    return bestEdge
      ? { wallId: selectedOpening.wallId, openingId: selectedOpening.openingId, edge: bestEdge }
      : null;
  }

  function updateHover(): void {
    hoverOpening = null;
    hoverOpeningHandle = null;
    if (!(deps.activeTool() === "select" && viewable()) || desktopMouse.camDragging)
      return;
    // The selected opening's resize handles take precedence (white hover ring).
    const h = pickResizeHandleScreen(desktopMouse.clientX, desktopMouse.clientY);
    if (h) {
      hoverOpeningHandle = h;
      hoverOpening = { wallId: h.wallId, openingId: h.openingId };
      return;
    }
    // Otherwise the opening whose body the cursor ray crosses — its border
    // whitens and a click selects it. Aim our own ray at the cursor: in the 2D
    // side elevation the floor-point helper bails without setting a ray.
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    hoverOpening = wallSystem.openingAtRayAny(_ray.ray.origin, _ray.ray.direction);
  }

  // The Select chain's opening steps (original order preserved):
  //  0a. A resize handle of the SELECTED opening (handles only show once an
  //      opening is selected) → arm the edge resize. A plain release toggles
  //      that edge's lock; a drag resizes it. Engage the SAME handle the hover
  //      ring is on (screen-space pick), so anything highlighted is grabbable.
  //  0b. A dim pill of the selected opening → consume the press so the click
  //      doesn't deselect it; the pill-click handler edits it in place.
  //  0c. A press anywhere on the opening BODY selects it (its border whitens,
  //      handles + dims appear) and arms a move-along-the-wall drag — only when
  //      the opening is the FRONTMOST thing under the cursor, so a model in
  //      front still wins, but one BEHIND the doorway is blocked. A plain click
  //      just selects; a drag slides it.
  function press(): boolean {
    if (!viewable()) return false;
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    if (hoverOpeningHandle) {
      openingResize = { ...hoverOpeningHandle };
      openingDragMoved = false;
      _dragStart.copy(desktopMouse.ndc);
      return true;
    }
    if (selectedOpening) {
      const ph = _ray.intersectObjects(
        Object.values(pills).map((l) => l.sprite),
        false,
      );
      if (ph.some((h) => h.object.visible)) return true;
    }
    if (hoverOpening) {
      const oh = wallSystem.openingAtRayAny(_ray.ray.origin, _ray.ray.direction);
      const placeHit = placementSystem.pickHit(_ray);
      if (oh && (placeHit === null || oh.distance <= placeHit.distance)) {
        selectedOpening = { wallId: oh.wallId, openingId: oh.openingId };
        openingResize = null;
        // An opening is its own selection — drop wall/placement/measurement AND
        // any marquee group so a door is never highlighted alongside anything.
        deps.dropOtherSelections();
        openingDrag = { wallId: oh.wallId, openingId: oh.openingId };
        openingDragMoved = false;
        _dragStart.copy(desktopMouse.ndc);
        return true;
      }
    }
    return false;
  }

  // The two drag continuations, mutually exclusive (resize wins, as before).
  // Claims the frame only under the original gates (armed + LMB held).
  function drag(): boolean {
    if (openingResize !== null && desktopMouse.lmb) {
      // A plain click on a resize circle just selects (shows dims); only a real
      // drag past the dead-zone resizes the edge.
      if (!openingDragMoved && _dragStart.distanceTo(desktopMouse.ndc) > 0.008) {
        openingDragMoved = true;
      }
      if (openingDragMoved) {
        _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
        wallSystem.resizeOpeningEdgeByRay(
          openingResize.wallId,
          openingResize.openingId,
          openingResize.edge,
          _ray.ray.origin,
          _ray.ray.direction,
        );
        deps.refreshSelectionUI();
      }
      return true;
    }
    if (openingDrag !== null && desktopMouse.lmb) {
      // A plain click just selects (editor shows); only a real drag past the
      // dead-zone slides the opening along the wall.
      if (!openingDragMoved && _dragStart.distanceTo(desktopMouse.ndc) > 0.008) {
        openingDragMoved = true;
      }
      if (openingDragMoved) {
        // Move toward the cursor: along the wall, plus up/down for windows.
        _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
        wallSystem.moveOpeningByRay(
          openingDrag.wallId,
          openingDrag.openingId,
          _ray.ray.origin,
          _ray.ray.direction,
        );
        deps.refreshSelectionUI();
      }
      return true;
    }
    return false;
  }

  const gestureArmed = (): boolean => openingDrag !== null || openingResize !== null;
  function clearGesture(): void {
    openingDrag = null;
    openingDragMoved = false;
    openingResize = null;
  }
  // Release tail (runs BEFORE the bulk gesture clear, as in the original): a
  // plain click (no drag) on a resize handle toggles that edge's lock (yellow ↔
  // red) — like a wall corner's padlock; a drag resized instead.
  function release(): void {
    if (openingResize !== null && !openingDragMoved) {
      wallSystem.toggleOpeningEdgeLock(
        openingResize.wallId,
        openingResize.openingId,
        openingResize.edge,
      );
      deps.commitFloor();
    }
  }

  // Outline the hovered and selected openings in WHITE (their border "turns
  // white"), and render the SELECTED opening's four resize handles (yellow; red
  // when an edge is locked; a white ring on the hovered handle).
  function updateHandles(): void {
    const view = deps.activeTool() === "select" && viewable();
    // White rectangle border around the hovered and/or selected opening.
    const bp = _borderGeo.attributes.position as THREE.BufferAttribute;
    let bv = 0;
    if (view && hoverOpening) bv = writeBorder(bp, bv, hoverOpening.wallId, hoverOpening.openingId);
    if (view && selectedOpening && !sameOpening(hoverOpening, selectedOpening)) {
      bv = writeBorder(bp, bv, selectedOpening.wallId, selectedOpening.openingId);
    }
    bp.needsUpdate = true;
    _borderGeo.setDrawRange(0, bv);
    border.visible = bv > 0;

    // Resize handles — only for the SELECTED opening.
    let ri = 0;
    hoverOutline.visible = false;
    const sel = view ? selectedOpening : null;
    const g = sel ? wallSystem.openingGeometry(sel.wallId, sel.openingId) : null;
    if (sel && g) {
      const { cx, cz, ux, uz, halfW, sill, top } = g;
      const midY = (sill + top) / 2;
      const hovEdge = sameOpening(hoverOpeningHandle, sel) ? hoverOpeningHandle!.edge : null;
      // Edge midpoints — all four for both doors and windows (a door's bottom
      // can be raised off the floor like a window's sill).
      const edges: [(typeof OPENING_EDGES)[number], number, number, number][] = [
        ["left", cx - ux * halfW, midY, cz - uz * halfW],
        ["right", cx + ux * halfW, midY, cz + uz * halfW],
        ["top", cx, top, cz],
        ["bottom", cx, sill, cz],
      ];
      for (const [edge, x, y, z] of edges) {
        const rh = poolHandle(ri++);
        rh.position.set(x, y, z);
        rh.userData = { wallId: sel.wallId, openingId: sel.openingId, edge } as OpeningEdgeRef;
        rh.visible = true;
        // A locked edge shows red (and can't be dragged — click it to unlock).
        rh.material = wallSystem.isOpeningEdgeLocked(sel.wallId, sel.openingId, edge) ? _lockMat : _resizeMat;
        if (hovEdge === edge) {
          hoverOutline.position.set(x, y, z);
          hoverOutline.visible = true;
        }
      }
    }
    for (let i = ri; i < handlePool.length; i++) handlePool[i].visible = false;
  }

  // Show the selected opening's dimensions, by which handle pair is active:
  //  • side resize  → width
  //  • top/bottom resize → height + head→ceiling + (windows) sill→floor
  //  • move → left/right gaps to the neighbours / wall ends
  // NB: NOT gated on the wall-dimensions toggle — a door/window's own
  // width/height/gap pills are part of editing the selected opening, not
  // "wall dimensions", so that toggle must not suppress them.
  function updateDims(): void {
    const sel = selectedOpening;
    if (sel === null || deps.activeTool() !== "select") {
      editorGroup.visible = false;
      return;
    }
    const view = dimView();
    const g = wallSystem.openingGeometry(sel.wallId, sel.openingId);
    if (!g) {
      editorGroup.visible = false;
      return;
    }
    editorGroup.visible = true;
    const { cx, cz, ux, uz, halfW, sill, top } = g;
    const fmt = (m: number): string => formatLengthIn(m * M_TO_IN, getDisplayUnit());
    const dp = _dimGeo.attributes.position as THREE.BufferAttribute;
    const editingField = deps.editingOpeningField(sel.openingId);
    // Write dimension-line segments, tracking the vertex count for setDrawRange.
    let nv = 0;
    const seg = (
      x0: number, y0: number, z0: number,
      x1: number, y1: number, z1: number,
    ): void => {
      dp.setXYZ(nv++, x0, y0, z0);
      dp.setXYZ(nv++, x1, y1, z1);
    };
    // Place + size a field pill, offset just OFF its line (perpendicular dir o*)
    // so a measurement shorter than the pill still shows its line. Hidden while
    // edited.
    for (const l of Object.values(pills)) l.sprite.visible = false;
    const placePill = (
      field: OpeningField,
      x: number, y: number, z: number,
      value: number,
      ox: number, oy: number, oz: number,
    ): void => {
      const l = pills[field];
      setLabel(l, fmt(value));
      const s = deps.pillScale(_tmpVec3.set(x, y, z));
      const hh = 0.155 * s * 0.5; // pill half-height
      // Offset off the line by the pill's half-extent ALONG the offset direction
      // (its width when pushed sideways off a vertical line, height when pushed
      // up/down off a horizontal one) + a small gap, so short lines stay visible.
      const d = (oy !== 0 ? hh : hh * l.aspect) + 0.155 * s * 0.22;
      l.sprite.position.set(x + ox * d, y + oy * d, z + oz * d);
      l.sprite.visible = editingField !== field;
      l.sprite.scale.set(0.155 * s * l.aspect, 0.155 * s, 1);
    };

    // Shared geometry + helpers.
    const TK = 0.08; // end-tick half length
    const H = wallSystem.height();
    // The head/sill/height chain reads OFF the wall entirely — a fixed margin
    // past the wall END nearest the opening (so it's never on the wall/handles).
    // Falls back to just left of the opening if the wall info is unavailable.
    const wi = wallSystem.info(sel.wallId);
    const L = wi ? Math.hypot(wi.bx - wi.ax, wi.bz - wi.az) : 0;
    const p = wi ? (cx - wi.ax) * ux + (cz - wi.az) * uz : 0; // opening param on the wall
    const nearA = p < L / 2; // nearer the start (vertex A) end?
    const endP = nearA ? -0.5 : L + 0.5; // 0.5 m past that end
    const vlx = wi ? wi.ax + ux * endP : cx - ux * (halfW + 0.3);
    const vlz = wi ? wi.az + uz * endP : cz - uz * (halfW + 0.3);
    const vDir = nearA ? -1 : 1; // pills face further out, away from the wall
    const vSpan = (y0: number, y1: number, field: OpeningField): void => {
      seg(vlx, y0, vlz, vlx, y1, vlz);
      seg(vlx - ux * TK, y0, vlz - uz * TK, vlx + ux * TK, y0, vlz + uz * TK);
      seg(vlx - ux * TK, y1, vlz - uz * TK, vlx + ux * TK, y1, vlz + uz * TK);
      placePill(field, vlx, (y0 + y1) / 2, vlz, Math.abs(y1 - y0), vDir * ux, 0, vDir * uz);
    };
    // Side-to-side gaps to the neighbours / wall ends. The dim line runs ALONG
    // the wall (ux,uz); it's lifted/pushed off the wall by `baseDist` along the
    // off-axis (ax,ay,az), which is also the direction ticks extend and pills
    // stack. In 3D/elevation that off-axis is straight up (+Y) so the chain
    // floats above the wall top; in the top-down plan it's the wall normal so
    // the chain lies flat just off the wall face. The opening's span sits
    // exactly between the gaps (leftEnd→rightStart), so with `withWidth` the
    // width pill slots into that chain too.
    const drawSideGaps = (
      withWidth: boolean,
      ax: number, ay: number, az: number, // unit off-axis (ticks/pills extend & stack along this)
      baseDist: number,                    // how far the dim line sits off the wall along the off-axis
      baseY: number,                       // base Y of the dim line (before the off-axis lift)
    ): void => {
      const gap = wallSystem.openingGaps(sel.wallId, sel.openingId);
      if (!gap) return;
      const bx = (t: number): number => gap.originX + ux * t + ax * baseDist;
      const bz = (t: number): number => gap.originZ + uz * t + az * baseDist;
      const by = baseY + ay * baseDist;
      const segs: [number, number, OpeningField][] = [[gap.leftStart, gap.leftEnd, "leftGap"]];
      if (withWidth) segs.push([gap.leftEnd, gap.rightStart, "width"]); // opening span
      segs.push([gap.rightStart, gap.rightEnd, "rightGap"]);
      // Pills are constant on-screen size, so a short segment's pill is wider
      // than its run and would overlap its neighbour. Stack overlapping pills
      // onto rows off the line (along the off-axis): each pill takes the lowest
      // row that clears the previous pill there (along-wall ≈ screen-x here).
      const sMid = deps.pillScale(_tmpVec3.set(cx, by, cz));
      const rowH = 0.155 * sMid * 1.25; // spacing between stacked pills, along the off-axis
      const rowRight: number[] = []; // right param-edge of the last pill on each row
      for (const [t0, t1, field] of segs) {
        seg(bx(t0), by, bz(t0), bx(t1), by, bz(t1));
        seg(bx(t0) - ax * TK, by - ay * TK, bz(t0) - az * TK, bx(t0) + ax * TK, by + ay * TK, bz(t0) + az * TK);
        seg(bx(t1) - ax * TK, by - ay * TK, bz(t1) - az * TK, bx(t1) + ax * TK, by + ay * TK, bz(t1) + az * TK);
        const len = Math.abs(t1 - t0);
        if (len < 1e-4) continue;
        const mid = (t0 + t1) / 2;
        const l = pills[field];
        setLabel(l, fmt(len)); // refresh aspect so the half-width below is current
        const halfPill = (0.155 * sMid * l.aspect) / 2 + 0.04; // + a small gap
        let row = 0;
        while (row < rowRight.length && rowRight[row] > mid - halfPill) row++;
        if (row === rowRight.length) rowRight.push(0);
        rowRight[row] = mid + halfPill;
        placePill(field, bx(mid) + ax * row * rowH, by + ay * row * rowH, bz(mid) + az * row * rowH, len, ax, ay, az);
      }
    };
    // Head→wall-top and (raised openings) sill→floor gaps, in the left vSpan chain.
    const drawVertGaps = (): void => {
      vSpan(top, H, "topGap");
      if (sill > 1e-3) vSpan(0, sill, "sillGap");
    };

    if (view === "plan") {
      // Bird's-eye plan: only the along-wall measurements make sense (height /
      // sill / head are edge-on and overlap). Lay the gap chain flat just off
      // the wall face, ticks/pills offset along the wall normal in the XZ plane.
      // Push a bit further out when the wall's own dim pill shares this face, so
      // the two chains clear each other instead of overlapping.
      const push = deps.wallDimPlanPush(sel.wallId);
      drawSideGaps(true, -uz, 0, ux, WALL_THICKNESS / 2 + 0.22 + push, 0.06);
    } else {
      // 3D / side elevation: the full chain. Width + side gaps float above the
      // wall top; height + head/sill gaps read off the wall end.
      drawSideGaps(true, 0, 1, 0, H + 0.2, 0);
      vSpan(sill, top, "height");
      drawVertGaps();
    }
    dp.needsUpdate = true;
    _dimGeo.setDrawRange(0, nv);
  }

  // Render width (+ height off the plan) dimension labels for EVERY opening when
  // the "show all dimensions" toggle is on — a read-only overlay independent of
  // the selected-opening editor. The currently selected opening is skipped (its
  // editor already shows richer pills). View rule: top-down plan → only the
  // along-wall width laid flat off the wall face; side elevation / 3D → width
  // above the head + height beside the jamb, and an elevation drops openings on
  // edge-on walls (their face isn't toward you).
  function updateAllDims(): void {
    if (!showAllDims) {
      allDimsGroup.visible = false;
      return;
    }
    const view = dimView();
    const fmt = (m: number): string => formatLengthIn(m * M_TO_IN, getDisplayUnit());
    // View direction (XZ, unit) for the elevation face-on test.
    let vdx = 0;
    let vdz = 0;
    if (view === "elevation") {
      deps.activeCamera().getWorldDirection(_ovDir);
      const vl = Math.hypot(_ovDir.x, _ovDir.z) || 1;
      vdx = _ovDir.x / vl;
      vdz = _ovDir.z / vl;
    }
    // Size the line buffer once for the worst case (12 verts/opening).
    let total = 0;
    for (const wid of wallSystem.wallIds()) total += wallSystem.openingsOf(wid).length;
    ensureAllDimCap(total * 12);
    const dp = _allDimGeo.attributes.position as THREE.BufferAttribute;
    const TK = 0.08; // end-tick half length
    const sel = selectedOpening;
    let nv = 0;
    let li = 0;
    const seg = (
      x0: number, y0: number, z0: number,
      x1: number, y1: number, z1: number,
    ): void => {
      dp.setXYZ(nv++, x0, y0, z0);
      dp.setXYZ(nv++, x1, y1, z1);
    };
    // Place + size one pooled pill just off its line (perpendicular dir o*).
    const pill = (
      l: SpriteLabel,
      x: number, y: number, z: number,
      value: number,
      ox: number, oy: number, oz: number,
    ): void => {
      setLabel(l, fmt(value));
      const s = deps.pillScale(_tmpVec3.set(x, y, z));
      const hh = 0.155 * s * 0.5;
      const d = (oy !== 0 ? hh : hh * l.aspect) + 0.155 * s * 0.22;
      l.sprite.position.set(x + ox * d, y + oy * d, z + oz * d);
      l.sprite.scale.set(0.155 * s * l.aspect, 0.155 * s, 1);
      l.sprite.visible = true;
    };

    for (const wid of wallSystem.wallIds()) {
      // Push plan dims a bit further off the wall when its own dim pill shares
      // this face, so the two chains clear each other (0 otherwise).
      const push = deps.wallDimPlanPush(wid);
      for (const o of wallSystem.openingsOf(wid)) {
        if (sel && sel.wallId === wid && sel.openingId === o.id) continue; // editor owns it
        const g = wallSystem.openingGeometry(wid, o.id);
        if (!g) continue;
        const { cx, cz, ux, uz, halfW, sill, top, width, height } = g;
        const lx = cx - ux * halfW; // left jamb foot (XZ)
        const lz = cz - uz * halfW;
        const rx = cx + ux * halfW; // right jamb foot
        const rz = cz + uz * halfW;
        const nx = -uz; // wall normal in XZ
        const nz = ux;
        if (view === "plan") {
          // Width laid flat just off the wall face; ticks/pill along the normal.
          const dd = WALL_THICKNESS / 2 + 0.22 + push;
          const y = 0.06;
          const ax = lx + nx * dd;
          const az = lz + nz * dd;
          const bx = rx + nx * dd;
          const bz = rz + nz * dd;
          seg(ax, y, az, bx, y, bz);
          seg(ax - nx * TK, y, az - nz * TK, ax + nx * TK, y, az + nz * TK);
          seg(bx - nx * TK, y, bz - nz * TK, bx + nx * TK, y, bz + nz * TK);
          pill(allDimLabel(li++), (ax + bx) / 2, y, (az + bz) / 2, width, nx, 0, nz);
        } else {
          // Side elevation: only openings whose wall faces the camera read true
          // (an edge-on wall foreshortens to nothing) — skip the rest.
          if (view === "elevation" && Math.abs(nx * vdx + nz * vdz) < 0.5) continue;
          // Width above the head.
          const yW = top + 0.12;
          seg(lx, yW, lz, rx, yW, rz);
          seg(lx, yW, lz, lx, yW - TK, lz);
          seg(rx, yW, rz, rx, yW - TK, rz);
          pill(allDimLabel(li++), (lx + rx) / 2, yW, (lz + rz) / 2, width, 0, 1, 0);
          // Height beside the left jamb (line off the jamb, ticks along the wall).
          const off = 0.14;
          const hx = lx - ux * off;
          const hz = lz - uz * off;
          seg(hx, sill, hz, hx, top, hz);
          seg(hx - ux * TK, sill, hz - uz * TK, hx + ux * TK, sill, hz + uz * TK);
          seg(hx - ux * TK, top, hz - uz * TK, hx + ux * TK, top, hz + uz * TK);
          pill(allDimLabel(li++), hx, (sill + top) / 2, hz, height, -ux, 0, -uz);
        }
      }
    }
    for (let i = li; i < allDimLabels.length; i++) allDimLabels[i].sprite.visible = false;
    dp.needsUpdate = true;
    _allDimGeo.setDrawRange(0, nv);
    allDimsGroup.visible = nv > 0;
  }

  // ── Dim-edit integration (the shared editor box lives in floor-mode) ──
  // Current value (metres) of an opening field, for prefilling the editor.
  function fieldValue(wallId: number, openingId: number, field: OpeningField): number | null {
    const o = wallSystem.openingProps(wallId, openingId);
    if (!o) return null;
    const H = wallSystem.height();
    if (field === "width") return o.width;
    if (field === "height") return o.height;
    if (field === "sillGap") return o.sill;
    if (field === "topGap") return H - (o.sill + o.height);
    const g = wallSystem.openingGaps(wallId, openingId);
    if (!g) return null;
    return field === "leftGap" ? g.leftEnd - g.leftStart : g.rightEnd - g.rightStart;
  }
  // Apply an edited opening field (metres) — size sets it directly, a gap
  // repositions the opening so that gap becomes the typed value.
  function applyField(wallId: number, openingId: number, field: OpeningField, m: number): void {
    const o = wallSystem.openingProps(wallId, openingId);
    if (!o) return;
    if (field === "width" || field === "height") {
      wallSystem.setOpeningDim(wallId, openingId, field, m);
    } else if (field === "sillGap") {
      // Move the sill, keep the head (so a locked top stays put) — bottom-handle.
      wallSystem.setOpeningSillGap(wallId, openingId, m);
    } else if (field === "topGap") {
      // Move the head, keep the sill (so a locked bottom stays put) — top-handle.
      wallSystem.setOpeningHeadGap(wallId, openingId, m);
    } else {
      // leftGap / rightGap. Honour jamb locks like the resize handles do:
      //  • the edited jamb's OWN lock → it can't move, so the edit is a no-op.
      //  • the OPPOSITE jamb locked → keep that jamb fixed and RESIZE to it
      //    (routed through setOpeningDim, which already pins the locked jamb).
      //  • neither locked → slide the whole opening so the gap becomes m.
      const g = wallSystem.openingGaps(wallId, openingId);
      if (!g) return;
      if (field === "leftGap") {
        if (o.lockLeft) return; // left jamb pinned — its gap can't move it
        const newLeft = g.leftStart + m;
        if (o.lockRight) {
          wallSystem.setOpeningDim(wallId, openingId, "width", o.offset + o.width / 2 - newLeft);
        } else {
          wallSystem.setOpeningOffset(wallId, openingId, newLeft + o.width / 2);
        }
      } else {
        if (o.lockRight) return; // right jamb pinned
        const newRight = g.rightEnd - m;
        if (o.lockLeft) {
          wallSystem.setOpeningDim(wallId, openingId, "width", newRight - (o.offset - o.width / 2));
        } else {
          wallSystem.setOpeningOffset(wallId, openingId, newRight - o.width / 2);
        }
      }
    }
  }
  // The visible pill under a (cursor-aimed) ray, for the click-to-edit.
  function pickPillField(ray: THREE.Raycaster): OpeningField | null {
    const oh = ray.intersectObjects(
      Object.values(pills).map((l) => l.sprite),
      false,
    );
    const hit = oh.find((h) => h.object.visible);
    return hit ? (hit.object.userData.field as OpeningField) : null;
  }

  return {
    selected: () => selectedOpening,
    setSelected: (sel) => {
      selectedOpening = sel;
    },
    hover: () => hoverOpening,
    hoverHandle: () => hoverOpeningHandle,
    clearSelection: () => {
      selectedOpening = null;
      openingResize = null;
    },
    viewable,
    updateHandles,
    updateDims,
    setShowAllDims: (on) => {
      showAllDims = on;
    },
    updateAllDims,
    fieldValue,
    applyField,
    pickPillField,
    pillPosition: (field) => pills[field].sprite.position,
    editorVisible: () => editorGroup.visible,
    updateHover,
    press,
    drag,
    gestureArmed,
    clearGesture,
    release,
  };
}
